import { Dialog, DialogActions, DialogContent, Drawer } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import MDButton from 'components/MDButton';
import { useNotification } from 'hook/useNotification';
import { useCreateBatchMutation, useUpdateBatchMutation } from 'services/dinaBatchApi';
import DinaBatchDetails from 'pages/knowledgeBase/batch/dinaBatchDetails';

function DinaBatchDetailsDrawer({ batch, drawerOpen, setDrawerOpen, knowledgeBase }) {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();

  const [createBatch] = useCreateBatchMutation();
  const [updateBatch] = useUpdateBatchMutation();

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [confirmCloseFormDialogOpen, setConfirmCloseFormDialogOpen] = useState(false);

  const toggleDrawer = (open) => () => {
    if (isFormDirty) {
      setConfirmCloseFormDialogOpen(true);
    } else {
      setDrawerOpen(open);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setConfirmCloseFormDialogOpen(false);
    setIsFormDirty(false);
  };

  const handleCancelClose = () => {
    setConfirmCloseFormDialogOpen(false);
  };

  const handleFormSubmit = (data) => {
    // Filter out empty parameters and format them correctly
    const filteredParameters =
      data.parameters
        ?.filter((param) => param.paramValue && param.paramValue.trim() !== '')
        .map((param) => ({
          batchParamType: param.batchParamType,
          paramValue: param.paramValue
        })) || [];

    const knowledgeBaseParamType = data.batchType?.paramTypes?.find(
      (paramType) => paramType.code === 'KNOWLEDGE_BASE_CODE'
    );

    if (knowledgeBaseParamType) {
      const knowledgeBaseParam = {
        batchParamType: knowledgeBaseParamType,
        paramValue: knowledgeBase?.code
      };

      filteredParameters.push(knowledgeBaseParam);
    }

    const formattedData = {
      ...data,
      parameters: filteredParameters
    };

    if (!data.id) {
      handleCreate(formattedData);
    } else {
      handleUpdate(formattedData);
    }
    setDrawerOpen(false);
    setIsFormDirty(false);
  };

  const handleFieldChange = (dirty) => {
    setIsFormDirty(dirty);
  };

  const handleCreate = (data) => {
    createBatch(data)
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.batch.create.success' })
        });
      })
      .catch((error) => {
        console.error('Create batch error:', error);
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.batch.create.error' })
        });
      });
  };

  const handleUpdate = (data) => {
    updateBatch({
      id: data.id,
      dinaBatchRequest: data
    })
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.batch.update.success' })
        });
      })
      .catch((error) => {
        console.error('Update batch error:', error);
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.batch.update.error' })
        });
      });
  };

  return (
    <>
      <Drawer
        variant="temporary"
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: { width: 700, p: 2 }
        }}
      >
        <DinaBatchDetails
          batch={batch}
          knowledgeBase={knowledgeBase}
          onSubmit={handleFormSubmit}
          onFieldChange={handleFieldChange}
        />
      </Drawer>
      <Dialog open={confirmCloseFormDialogOpen} onClose={handleCancelClose}>
        <DialogTitle>{msg({ id: 'dialog.unsaved_changes.title' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{msg({ id: 'dialog.unsaved_changes.message' })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCancelClose} color="secondary">
            {msg({ id: 'button.cancel' })}
          </MDButton>
          <MDButton onClick={handleCloseDrawer} color="primary">
            {msg({ id: 'button.discard' })}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

DinaBatchDetailsDrawer.propTypes = {
  batch: PropTypes.shape({
    id: PropTypes.number,
    batchType: PropTypes.object,
    cron: PropTypes.string,
    parameters: PropTypes.array
  }),
  drawerOpen: PropTypes.bool.isRequired,
  setDrawerOpen: PropTypes.func.isRequired,
  knowledgeBase: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string
  })
};

export default DinaBatchDetailsDrawer;
